import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Tooltip,
} from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { DateTime } from 'luxon';
import React from 'react';
import { match } from 'ts-pattern';
import ArcticonsTwilight from '~icons/arcticons/twilight';
import Fa6BrandsSith from '~icons/fa6-brands/sith';
import Fa6SolidJedi from '~icons/fa6-solid/jedi';
import TablerIconArrowBackUp from '~icons/tabler/arrow-back-up';
import { IconAvatar } from '#/components/Avatars';
import { DialogTitle } from '#/components/dialog';
import { useUserSettings } from '#/features/user';
import { useStorageItem } from '#/lib/storage';
import { cn } from '#/utils/styles';
import ThemeSwitch from './theme-switch';

/**
 * Allow users to migrate from the old theme system to the new one.
 */
function ThemeMigration() {
  const userSettings = useUserSettings();
  const [showDialog, setShowDialog] = React.useState(false);
  const migratedState = useStorageItem<boolean>(
    'settings::appearance::mode::migrated'
  );

  const themeMode = React.useMemo(() => {
    return userSettings.data.theme ?? 'dark';
  }, [userSettings.data.theme]);

  const migrated = React.useMemo(() => {
    // We want to ignore light as there is no migration required.
    if (userSettings.data.theme === 'light') {
      return true;
    }
    return migratedState.data ?? false;
  }, [migratedState.data, userSettings.data.theme]);

  const handleClose = React.useCallback(() => {
    setShowDialog(false);
    migratedState.set(true);
  }, [migratedState]);

  React.useEffect(() => {
    if (!migrated && !showDialog && !isExpired()) {
      // We don't pop-up the dialog immediately, as it may cause a flash of the
      setTimeout(() => {
        setShowDialog(true);
      }, 4000);
    }
  }, [migrated, showDialog]);

  if (isExpired()) {
    return null;
  }

  return (
    <React.Fragment>
      <Dialog open={showDialog}>
        <DialogTitle
          startIcon={<IconAvatar icon='palette' />}
          title='Dark Mode'
          subTitle='A new dark mode is available!'
          onClose={handleClose}
        />
        <DialogContent className='flex flex-col gap-5'>
          <DialogContentText>
            We just released a new <code>dark</code> theme to replace the{' '}
            "legacy dark" theme. The "legacy dark" theme is now a standalone
            theme: <code>twilight</code>.
          </DialogContentText>
          <ThemeSwitch
            onChange={(theme) => {
              userSettings.onUpdate({ theme });
            }}
            label='Switch to your preferred theme'
          />
          <DialogContentText>
            Will you revert to your old ways, or will you embrace the dark side?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Tooltip
            title={match(themeMode)
              .with('light', () => 'Confirm switch to light mode' as const)
              .with(
                'twilight',
                () => 'Confirm switch to twilight mode' as const
              )
              .with('dark', () => 'Confirm switch to dark mode' as const)
              .exhaustive()}
            placement='bottom'
            enterDelay={600}
            arrow
          >
            <Button
              color={match(themeMode)
                .with('light', () => 'primary' as const)
                .with('twilight', () => 'secondary' as const)
                .with('dark', () => 'error' as const)
                .exhaustive()}
              className={cn({
                'bg-black hover:opacity-80': themeMode === 'dark',
              })}
              startIcon={match(themeMode)
                .with('light', () => <Fa6SolidJedi />)
                .with('twilight', () => (
                  <ArcticonsTwilight className='stroke-4' />
                ))
                .with('dark', () => <Fa6BrandsSith className='text-red-500' />)
                .exhaustive()}
              onClick={() => {
                userSettings.onUpdate({
                  theme: userSettings.data.theme,
                });
                handleClose();
              }}
            >
              {match(themeMode)
                .with('light', () => 'Luminous beings are we' as const)
                .with('twilight', () => 'Live in the twilight' as const)
                .with('dark', () => 'Join the dark side' as const)
                .exhaustive()}
            </Button>
          </Tooltip>
          <Button
            variant='outlined'
            onClick={() => {
              handleClose();
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

export default () => {
  return (
    <ErrorBoundary>
      <ThemeMigration />
    </ErrorBoundary>
  );
};

/*
|------------------
| Internals
|------------------
*/

/**
 * Check if the migration is expired (we are setting a date of late May to force it)
 */
function isExpired() {
  const today = DateTime.local();
  const targetDate = DateTime.fromISO('2025-05-30');
  const isBefore = today < targetDate;
  return !isBefore;
}
